import React, { useEffect, useState } from 'react';
import { minisites, brands as brandsApi } from '@/utils/api';
import { Globe, Plus, ExternalLink, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function MiniSites() {
  const [brands, setBrands] = useState([]);
  const [sites, setSites] = useState([]);
  const [editingBrand, setEditingBrand] = useState(null);

  const reload = () => minisites.list().then(r => setSites(r.data));
  useEffect(() => {
    brandsApi.list().then(r => setBrands(r.data));
    reload();
  }, []);

  const openEdit = (brand_id) => {
    const existing = sites.find(s => s.brand_id === brand_id);
    setEditingBrand({
      brand_id,
      tagline: existing?.tagline || '',
      hero_md: existing?.hero_md || '',
      case_studies: existing?.case_studies || [],
      cta_text: existing?.cta_text || 'Book a call',
      cta_url: existing?.cta_url || '',
      contact_email: existing?.contact_email || '',
      is_published: existing?.is_published || false,
    });
  };

  const save = async () => {
    try {
      await minisites.upsert(editingBrand);
      toast.success('Saved');
      setEditingBrand(null);
      reload();
    } catch (e) { toast.error('Failed'); }
  };

  const addCaseStudy = () => {
    setEditingBrand({
      ...editingBrand,
      case_studies: [...(editingBrand.case_studies || []), { title: '', summary_md: '', result: '' }],
    });
  };
  const updateCS = (idx, field, val) => {
    const cs = [...editingBrand.case_studies];
    cs[idx] = { ...cs[idx], [field]: val };
    setEditingBrand({ ...editingBrand, case_studies: cs });
  };
  const removeCS = (idx) => {
    setEditingBrand({ ...editingBrand, case_studies: editingBrand.case_studies.filter((_, i) => i !== idx) });
  };

  return (
    <div className="p-6 animate-fade-in" data-testid="minisites-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-btn flex items-center justify-center" style={{ background: '#006080' + '20' }}>
          <Globe size={18} style={{ color: '#006080' }} />
        </div>
        <div>
          <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Brand Mini-Sites</h1>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>A public landing per brand — case studies + CTA</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3" data-testid="brands-list">
        {brands.map(b => {
          const site = sites.find(s => s.brand_id === b.id);
          return (
            <div key={b.id} className="card-base p-4" data-testid={`minisite-brand-${b.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{b.name}</p>
                  <p className="text-xs font-mono" style={{ color: 'var(--muted)' }}>/public/sites/{b.slug}</p>
                </div>
                {site?.is_published ? (
                  <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>Published</span>
                ) : (
                  <span className="badge-status" style={{ background: '#8A99A015', color: '#5C6B72' }}>Draft</span>
                )}
              </div>
              {site && (
                <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--muted)' }}>{site.tagline || site.hero_md || 'No content yet'}</p>
              )}
              <div className="flex gap-2">
                <button className="text-xs px-3 py-1.5 rounded-btn font-semibold" style={{ background: '#E6F1F4', color: '#006080' }} onClick={() => openEdit(b.id)} data-testid={`edit-site-${b.id}`}>
                  Edit
                </button>
                {site?.is_published && (
                  <a href={`/sites/${b.slug}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                    <ExternalLink size={11} /> Preview
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editingBrand && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-3xl rounded-card my-8 p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-black text-lg" style={{ color: 'var(--ink)' }}>Mini-site editor</h2>
              <button onClick={() => setEditingBrand(null)} style={{ color: 'var(--muted)' }}>✕</button>
            </div>
            <div className="space-y-3">
              <input className="input-agency text-sm" placeholder="Tagline" value={editingBrand.tagline} onChange={e => setEditingBrand({...editingBrand, tagline: e.target.value})} data-testid="site-tagline" />
              <textarea className="input-agency text-sm resize-none" rows={5} placeholder="Hero body (markdown)" value={editingBrand.hero_md} onChange={e => setEditingBrand({...editingBrand, hero_md: e.target.value})} data-testid="site-hero" />
              <div className="grid grid-cols-2 gap-2">
                <input className="input-agency text-sm" placeholder="CTA text" value={editingBrand.cta_text} onChange={e => setEditingBrand({...editingBrand, cta_text: e.target.value})} />
                <input className="input-agency text-sm" type="url" placeholder="CTA URL" value={editingBrand.cta_url} onChange={e => setEditingBrand({...editingBrand, cta_url: e.target.value})} />
              </div>
              <input className="input-agency text-sm" type="email" placeholder="Contact email" value={editingBrand.contact_email} onChange={e => setEditingBrand({...editingBrand, contact_email: e.target.value})} />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold uppercase" style={{ color: 'var(--muted)' }}>Case studies</p>
                  <button className="text-xs flex items-center gap-1" style={{ color: '#006080' }} onClick={addCaseStudy} data-testid="add-case-study"><Plus size={10} /> Add</button>
                </div>
                <div className="space-y-2">
                  {(editingBrand.case_studies || []).map((cs, i) => (
                    <div key={i} className="p-2 rounded-btn space-y-1" style={{ background: 'var(--bg)' }}>
                      <input className="input-agency text-sm" placeholder="Title" value={cs.title} onChange={e => updateCS(i, 'title', e.target.value)} />
                      <textarea className="input-agency text-sm resize-none" rows={2} placeholder="Summary" value={cs.summary_md} onChange={e => updateCS(i, 'summary_md', e.target.value)} />
                      <div className="flex gap-2">
                        <input className="input-agency text-sm flex-1" placeholder="Result (e.g., 40% faster)" value={cs.result} onChange={e => updateCS(i, 'result', e.target.value)} />
                        <button className="text-xs px-2" style={{ color: '#B34A3A' }} onClick={() => removeCS(i)}>remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
                <input type="checkbox" checked={editingBrand.is_published} onChange={e => setEditingBrand({...editingBrand, is_published: e.target.checked})} data-testid="site-publish" />
                Publish this mini-site
              </label>

              <div className="flex gap-2 pt-2">
                <button className="flex-1 py-2 text-sm rounded-btn border" onClick={() => setEditingBrand(null)} style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>Cancel</button>
                <button className="flex-1 btn-primary py-2 text-sm flex items-center justify-center gap-1.5" onClick={save} data-testid="site-save"><Save size={13} /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
